import { z } from 'zod';
import { idParamSchema, nameAndCode } from './common';
import { cultureOutcomeSchema, growthGradeSchema, susceptibilitySchema } from './enums';

/** An antibiotic a sensitivity panel can test. */
export const createAntibioticSchema = z.object(nameAndCode);
export type CreateAntibioticInput = z.infer<typeof createAntibioticSchema>;
export const updateAntibioticSchema = createAntibioticSchema;
export type UpdateAntibioticInput = z.infer<typeof updateAntibioticSchema>;

/** An organism a culture can isolate. */
export const createOrganismSchema = z.object(nameAndCode);
export type CreateOrganismInput = z.infer<typeof createOrganismSchema>;
export const updateOrganismSchema = createOrganismSchema;
export type UpdateOrganismInput = z.infer<typeof updateOrganismSchema>;

const sensitivityEntrySchema = z.object({
  antibiotic_id: idParamSchema,
  /** Null while not yet read. Such a row is kept on the draft and not printed. */
  result: susceptibilitySchema.nullable(),
});

/**
 * A culture report's findings, as the entry screen saves them — whole.
 *
 * `sensitivities` order is print order. Organism, growth and colony count
 * describe a growth; they are cleared by the server when the outcome is "no
 * growth", so a report cannot print an organism under "No growth".
 */
export const cultureEntrySchema = z.object({
  specimen: z.string().trim().default(''),
  outcome: cultureOutcomeSchema.nullable(),
  organism_id: idParamSchema.nullable(),
  growth: growthGradeSchema.nullable(),
  colony_count: z.string().trim().default(''),
  sensitivities: z
    .array(sensitivityEntrySchema)
    .default([])
    .refine(
      (rows) => new Set(rows.map((row) => row.antibiotic_id)).size === rows.length,
      'Each antibiotic can be on the panel only once',
    ),
});
export type CultureEntryInput = z.infer<typeof cultureEntrySchema>;
